import React from "react"
import clsx from "clsx"
import {
	Grid,
	Typography,
	Popper,
	Paper,
	List,
	ListItemIcon,
	ListItemText,
	ListSubheader,
	Tooltip
} from "@material-ui/core"
import { InfoOutlined as InfoIcon } from "@material-ui/icons"
import { ChevronRight as ChevronRightIcon, User as UserIcon } from "lucide-react"
import { Link } from "react-router-dom"

import { colors } from "@/styles/colors"

import ListItemMenu from "@/components/ListItemMenu"
import { MenuCategory } from "@/hooks/useDashboardMenuData"
import { useGlobalStateStore } from "@/store/GlobalState"
import useStyles from "@/@integrations/Whatsapp/components/Menu/NewSideBar/UserProfileItem/styles"

type UserProfileItemProps = {
	accountMenuCategory: MenuCategory
	anchorRefs: React.MutableRefObject<Record<string, HTMLElement | null>>
	hoveredCategory: string | null
	handleEnter: (id: string) => void
	handleLeave: () => void
	handleClick: (id: string) => void
	isSidebarOpen: boolean
}

const UserProfileItem: React.FC<UserProfileItemProps> = ({
	accountMenuCategory,
	anchorRefs,
	hoveredCategory,
	handleEnter,
	handleLeave,
	handleClick,
	isSidebarOpen
}) => {
	const classes = useStyles()
	const globalStateStore = useGlobalStateStore()
	const isOpen = hoveredCategory === accountMenuCategory.id

	const userName = globalStateStore.user.name ?? ""
	const userEmail = globalStateStore.user.email ?? ""

	const buttonContent = (
		<Grid
			container
			alignItems="center"
			className={clsx(classes.button, { [classes.buttonCollapsed]: !isSidebarOpen })}
			onMouseEnter={() => handleEnter(accountMenuCategory.id)}
			onMouseLeave={handleLeave}
			onClick={() => handleClick(accountMenuCategory.id)}
			ref={(element: HTMLDivElement | null) => {
				if (anchorRefs.current) {
					anchorRefs.current[accountMenuCategory.id] = element
				}
			}}
		>
			<Grid container alignItems="center" justifyContent="center" className={classes.avatarCircle}>
				<UserIcon size={18} />
			</Grid>

			{isSidebarOpen && (
				<>
					<Grid item className={classes.textBlock}>
						<Typography className={classes.nameText}>{userName}</Typography>
						<Typography className={classes.emailText}>{userEmail}</Typography>
					</Grid>
					<Grid item>
						<ChevronRightIcon size={16} className={classes.chevronIcon} />
					</Grid>
				</>
			)}
		</Grid>
	)

	return (
		<>
			{buttonContent}
			<Popper
				open={isOpen}
				anchorEl={anchorRefs.current[accountMenuCategory.id]}
				placement={accountMenuCategory.placement ?? "right-end"}
				disablePortal={false}
				className={classes.popperContainer}
				onMouseEnter={() => handleEnter(accountMenuCategory.id)}
				onMouseLeave={handleLeave}
			>
				<Paper elevation={3} className={classes.popperPaper}>
					<List>
						<ListSubheader disableSticky>{accountMenuCategory.label}</ListSubheader>
						{accountMenuCategory.items?.map((subitem) => {
							const itemContent = subitem?.onClick
								? <ListItemMenu
									key={subitem.label}
									text={subitem.label}
									featureType={subitem.featureType}
									to={subitem.to}
									isExternalLink={subitem.isExternal}
									isDisabled={subitem.isDisabled}
									listItemIcon={
										<ListItemIcon className={classes.listItemIcon}>
											{subitem.icon}
										</ListItemIcon>
									}
									listItemText={
										<ListItemText>
											<Typography className={classes.itemTextTypography}>
												{subitem.label}
											</Typography>
										</ListItemText>
									}
									onClick={subitem.onClick}
								/>
								: <Link
									key={subitem.label}
									to={subitem.to || "/"}
									target={subitem.isExternal ? "_blank" : "_self"}
									className={classes.styledSubLink}
									style={subitem.isDisabled ? { pointerEvents: "none" } : undefined}
								>
									<ListItemMenu
										text={subitem.label}
										featureType={subitem.featureType}
										to={subitem.to}
										isExternalLink={subitem.isExternal}
										isDisabled={subitem.isDisabled}
										listItemIcon={
											<ListItemIcon className={classes.listItemIcon}>
												{subitem.icon}
											</ListItemIcon>
										}
										listItemText={
											<ListItemText>
												<Typography className={classes.itemTextTypography}>
													{subitem.label}
												</Typography>
											</ListItemText>
										}
									/>
								</Link>

							if (subitem.isDisabled && subitem.disabledTooltip) {
								return (
									<Grid key={subitem.label} container alignItems="center" justifyContent="space-between" wrap="nowrap">
										<Grid item className={classes.disabledItem} style={{ flex: 1, minWidth: 0 }}>
											{itemContent}
										</Grid>
										<Grid item className={classes.disabledInfoIcon}>
											<Tooltip title={subitem.disabledTooltip}>
												<InfoIcon fontSize="small" htmlColor={colors.gray[400]} />
											</Tooltip>
										</Grid>
									</Grid>
								)
							}

							return itemContent
						})}
					</List>
				</Paper>
			</Popper>
		</>
	)
}

export default UserProfileItem
